import type { Color } from "./types";

export interface TimeControl {
  readonly initialSeconds: number;
  readonly incrementSeconds: number;
}

export interface TimeControlPreset {
  readonly id: string;
  readonly label: string;
  /** `null` = partie sans limite de temps (comportement historique du jeu). */
  readonly control: TimeControl | null;
}

export const TIME_CONTROL_PRESETS: readonly TimeControlPreset[] = [
  { id: "unlimited", label: "Illimite", control: null },
  { id: "1+0", label: "1 minute", control: { initialSeconds: 60, incrementSeconds: 0 } },
  { id: "3+0", label: "3 minutes", control: { initialSeconds: 180, incrementSeconds: 0 } },
  { id: "3+2", label: "3 min + 2 s", control: { initialSeconds: 180, incrementSeconds: 2 } },
  { id: "5+0", label: "5 minutes", control: { initialSeconds: 300, incrementSeconds: 0 } },
  { id: "5+3", label: "5 min + 3 s", control: { initialSeconds: 300, incrementSeconds: 3 } },
  { id: "10+0", label: "10 minutes", control: { initialSeconds: 600, incrementSeconds: 0 } },
  { id: "15+10", label: "15 min + 10 s", control: { initialSeconds: 900, incrementSeconds: 10 } },
  { id: "30+0", label: "30 minutes", control: { initialSeconds: 1800, incrementSeconds: 0 } },
];

export function resolveTimeControl(id: string): TimeControl | null {
  return TIME_CONTROL_PRESETS.find((preset) => preset.id === id)?.control ?? null;
}

/** Formate un temps restant en `M:SS` (secondes toujours sur 2 chiffres). */
export function formatClockTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export interface GameClockCallbacks {
  /** Appele a chaque changement de temps restant (tick periodique, incrementation, pause). */
  onTick: (remaining: Record<Color, number>) => void;
  /** Appele une seule fois, quand le temps d'un camp tombe a 0. */
  onFlag: (color: Color) => void;
}

/**
 * Chrono a la Fischer (temps initial + increment ajoute apres chaque coup joue), un
 * camp actif a la fois. Base sur des horodatages reels (`now`, injectable pour les
 * tests) plutot qu'un decompte pas a pas : reste exact meme si l'intervalle qui
 * appelle `tick()` est retarde (onglet en arriere-plan, calcul IA bloquant...).
 */
export class GameClock {
  private readonly remainingMs: Record<Color, number>;
  private active: Color | null = null;
  private lastTimestamp = 0;
  private intervalId: number | undefined;

  constructor(
    private readonly control: TimeControl,
    private readonly callbacks: GameClockCallbacks,
    private readonly now: () => number = () => performance.now(),
  ) {
    const initialMs = control.initialSeconds * 1000;
    this.remainingMs = { w: initialMs, b: initialMs };
    this.callbacks.onTick({ ...this.remainingMs });
  }

  getRemaining(color: Color): number {
    return this.remainingMs[color];
  }

  /** Demarre le decompte reel (setInterval) : a appeler une seule fois, jamais dans les tests. */
  startTicking(intervalMs = 250): void {
    this.intervalId = setInterval(() => this.tick(), intervalMs) as unknown as number;
  }

  /** Fige definitivement le chrono (fin de partie) et libere l'intervalle. */
  dispose(): void {
    this.pause();
    clearInterval(this.intervalId);
  }

  /** Reprend le decompte pour `color` (debut de son trait). */
  resume(color: Color): void {
    this.active = color;
    this.lastTimestamp = this.now();
  }

  /** Fige le decompte au temps ecoule reel (fin du trait en cours). Sans effet si deja en pause. */
  pause(): void {
    this.tick();
    this.active = null;
  }

  addIncrement(color: Color): void {
    this.remainingMs[color] += this.control.incrementSeconds * 1000;
    this.callbacks.onTick({ ...this.remainingMs });
  }

  /** Publique pour rester testable sans mocker `setInterval` ; sans effet si aucun camp actif. */
  tick(): void {
    if (this.active === null) return;
    const now = this.now();
    const elapsed = now - this.lastTimestamp;
    this.lastTimestamp = now;
    const color = this.active;
    this.remainingMs[color] = Math.max(0, this.remainingMs[color] - elapsed);
    this.callbacks.onTick({ ...this.remainingMs });
    if (this.remainingMs[color] <= 0) {
      this.active = null;
      this.callbacks.onFlag(color);
    }
  }
}
